// "Calls, Receivables and Payables" combo renderer (RET-4289).
//
// Grouped Receivables + Payables gradient bars with a cyan area-fill Calls line on top,
// plus an optional dashed Receivables % overlay. Registered in the charts_js_controller
// renderer registry (CUSTOM_RENDERERS); the Chart.js canvas legend is disabled and the
// legend is rendered as footer chips by the SAME htmlLegend plugin every other chart uses
// — the controller passes it in via `plugins` and builds the chips from Chart.js' default
// legend labels (line series get a line swatch, bars a box; see charts_js_controller).
//
// Two non-persisted knobs come from the card's header controls via charts_js_controller:
//   • mode: 'counts'  → bars are receivable/payable call COUNTS on one shared value axis.
//           'amounts' → bars are $ (data.amounts); the Calls line stays a COUNT on its
//           own right-hand axis so the dollar scale doesn't crush it.
//   • showReceivables → overlay the dashed Receivables % line (receivable calls ÷ calls,
//           derived here) on a hidden 0–100 axis.
//
// Colors come entirely from the themed palette (charts/palette.js → custom/charts.scss CSS
// variables), so they flip with the active color mode, and the global Chart.js defaults
// (theme.js: text/grid color, tooltip styling, 2px line stroke) carry the baseline — we
// only override what's specific to this chart.
//
// Reads the SAME `data.datasets` (counts) the /mobile/v1/calls_charts API produces,
// picking series by label, plus the additive web-only `data.amounts` sibling.
import { Chart } from 'chart.js'

import { chartPalette, barDepthFill, verticalGradient, BAR_DEPTH_BORDER_WIDTH } from './palette'
import { formatDollar } from '../app/modules/utilities'

// The data layer ships the three count series as Chart.js datasets keyed by label
// ("Calls" / "Receivables" / "Payables"); pull each out by name so we stay
// decoupled from their order.
function seriesByLabel(datasets, label) {
    const match = datasets.find((dataset) => dataset.label === label)
    return (match && match.data) || []
}

// Receivable rate per slot, as a 0–100 percentage of calls that were receivable.
function receivableRates(calls, receivables) {
    return calls.map((total, i) => {
        const n = Number(total) || 0
        const r = Number(receivables[i]) || 0
        return n > 0 ? Math.round((r / n) * 100) : 0
    })
}

// Compact money for axis ticks ($1.2k) — combo-specific; full dollars in tooltips reuse the
// shared formatDollar so amounts read identically to every other dollar-formatted chart.
function moneyTick(n) {
    const v = Number(n) || 0
    return v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${v.toFixed(0)}`
}

export function buildCallsReceivablesAndPayablesCombo(
    canvas,
    optionsFromParam,
    { mode = 'counts', showReceivables = false, plugins = [] } = {},
) {
    const { datasets: sourceDatasets = [], labels = [], amounts = {} } = optionsFromParam.data || {}
    const palette = chartPalette()
    const isAmounts = mode === 'amounts'

    const callsCounts = seriesByLabel(sourceDatasets, 'Calls')
    const receivablesCounts = seriesByLabel(sourceDatasets, 'Receivables')
    const payablesCounts = seriesByLabel(sourceDatasets, 'Payables')

    // $ series ride alongside the counts (additive `amounts` key); fall back to
    // counts if a client somehow lacks them so the chart still renders.
    const receivablesBars = isAmounts ? amounts.receivables || receivablesCounts : receivablesCounts
    const payablesBars = isAmounts ? amounts.payables || payablesCounts : payablesCounts

    const barBase = {
        type: 'bar',
        yAxisID: 'y',
        borderWidth: BAR_DEPTH_BORDER_WIDTH,
        barPercentage: 0.92,
        categoryPercentage: 0.78,
        borderRadius: { topLeft: 3, topRight: 3, bottomLeft: 0, bottomRight: 0 },
    }

    const datasets = [
        {
            ...barBase,
            label: 'Receivables',
            data: receivablesBars,
            // Shared bar depth gradient (lit top → translucent base), the same fill every
            // other bar chart uses; only the hue differs per series.
            backgroundColor: barDepthFill(palette.green),
            borderColor: palette.green,
            order: 2,
        },
        {
            ...barBase,
            label: 'Payables',
            data: payablesBars,
            backgroundColor: barDepthFill(palette.amber),
            borderColor: palette.amber,
            order: 2,
        },
        {
            type: 'line',
            label: 'Calls',
            // Calls is always a count. In $ mode it rides on its own axis so the
            // dollar scale on `y` doesn't flatten it.
            yAxisID: isAmounts ? 'yCalls' : 'y',
            data: callsCounts,
            borderColor: palette.callsLine,
            // Cyan area fill whose stops flip with color mode (charts.scss).
            backgroundColor: verticalGradient([
                [0, palette.areaFillTop],
                [1, palette.areaFillBottom],
            ]),
            tension: 0.35,
            pointRadius: 0,
            pointHoverRadius: 4,
            pointBackgroundColor: palette.callsLine,
            pointBorderColor: '#fff',
            pointBorderWidth: 1.5,
            borderJoinStyle: 'round',
            fill: true,
            order: 1,
        },
    ]

    if (showReceivables) {
        datasets.push({
            type: 'line',
            label: 'Receivables %',
            yAxisID: 'yReceivables',
            data: receivableRates(callsCounts, receivablesCounts),
            borderColor: palette.violet,
            borderDash: [5, 5],
            fill: false,
            tension: 0.35,
            pointRadius: 0,
            pointHoverRadius: 3,
            pointBackgroundColor: palette.violet,
            order: 0,
        })
    }

    return new Chart(canvas, {
        // Inline plugins from the controller (the shared htmlLegend renders the footer chips).
        plugins: [...plugins],
        data: { labels, datasets },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            resizeDelay: 0,
            interaction: { mode: 'index', intersect: false },
            plugins: {
                // Canvas legend stays off; the shared htmlLegend builds the footer chips from
                // Chart.js' default legend labels (line-vs-box swatch handled generically there).
                legend: { display: false },
                // Tooltip chrome (dark cyan-bordered box) comes from the global theme; only the
                // value formatting is chart-specific.
                tooltip: {
                    callbacks: {
                        label: (item) => {
                            const value = item.parsed.y
                            if (item.dataset.label === 'Receivables %') {
                                return `Receivables %: ${value}%`
                            }
                            const formatted = isAmounts
                                ? formatDollar(value)
                                : Number(value).toLocaleString()
                            return `${item.dataset.label}: ${formatted}`
                        },
                    },
                },
            },
            scales: {
                // Grid/border/tick colors come from the global defaults (theme.js); we only set
                // the structural bits this chart needs.
                x: {
                    stacked: false,
                    grid: { drawTicks: false },
                    ticks: {
                        maxRotation: 0,
                        autoSkip: true,
                        maxTicksLimit: 12,
                    },
                },
                // Primary value axis: counts, or $ in amounts mode.
                y: {
                    beginAtZero: true,
                    grid: { drawTicks: false },
                    border: { display: false },
                    ticks: {
                        padding: 8,
                        callback: (value) => (isAmounts ? moneyTick(value) : value),
                    },
                },
                // Right-hand count axis for the Calls line — only present in $ mode.
                yCalls: {
                    display: isAmounts,
                    beginAtZero: true,
                    position: 'right',
                    grid: { drawOnChartArea: false },
                    border: { display: false },
                    ticks: { color: palette.callsLine, padding: 6 },
                },
                // Hidden 0–100 axis for the dashed receivables % line.
                yReceivables: { display: false, min: 0, max: 100 },
            },
            animation: { duration: 320, easing: 'easeOutCubic' },
        },
    })
}
